import React from 'react'
import { Theme, withStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import { Grid, makeStyles } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import close from '../assets/images/colosePopup.svg'

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: 0,
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

interface WhatIfDataTooltipModalProps {
  open?: any
  setOpen?: any
  handleClose?: any
}
const useStyles = makeStyles({
  content: {
    background: '#FFFFFF',
    boxSizing: 'border-box',
    borderRadius: 3,
    maxWidth: 820,
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 24px 24px 24px',
  },
  title: {
    color: '#181818',
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 600,
  },
  subTitle: {
    color: '#5E5E5E',
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 400,
  },
  detailContent: {
    marginBottom: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 12,
    marginBottom: 16,
    borderBottom: '1px solid #DFE4EB',
    '& input': {
      width: 36,
      height: 36,
      borderRadius: 999,
      background: '#F2F2F2',
      boxSizing: 'border-box',
    },
  },
  body: {
    overflowY: 'auto',
    paddingRight: 4,
  },
  typeHead: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 700,
    color: '#1B3963',
  },
  typeDetail: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '22px',
    color: '#475569',
    '& b': {
      fontWeight: 700,
      color: '#1B3963',
    },
  },
})

const WhatIfDataTooltipModal = ({
  open,
  setOpen,
  handleClose,
}: WhatIfDataTooltipModalProps) => {
  const classes = useStyles()
  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth={false}
        PaperProps={{ style: { maxWidth: 820, width: '100%' } }}
      >
        <DialogContent>
          <div className={classes.content}>
            <div className={classes.header}>
              <div>
                <span className={classes.title}>What If Scenario</span>
              </div>
              <input
                type="image"
                onClick={() => handleClose()}
                src={close}
                alt="closeBtn"
              />
            </div>
            <div className={classes.body}>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>What-If: </span>
                <span className={classes.typeDetail}>
                  This gives you the ability to model your fundamental analysis
                  and future view of the company to ascertain what the new
                  credit stress measure would be. You have access to Blue Fire
                  AI's sector specific models which are more performant than any
                  universal model traditionally available in the industry. Once
                  you have simulated your assumptions on future company
                  variables, you also receive an instant peer benchmarking,
                  highlighting credit stress measure (CSM), Market Value of
                  Assets, Market Value of Leverage and Asset Volatility against
                  a peer group with similar exposures to underlying business
                  cycles, macro and interest rate factors, sector dynamics and
                  intrinsic credit risk. This simulation can help inform both
                  risk mitigation as well as relative value based performance
                  harvesting investment decisions.
                </span>
              </div>
              <br />
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>How to Use: </span>
                <span className={classes.typeDetail}>
                  There are two steps available;
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Step 1: </span>
                <span className={classes.typeDetail}>
                  gives you the ability to change observable drivers, typically
                  short term debt, long term debt, share price and equity
                  volatility. You can model the following scenarios
                </span>
                <br />
                <div
                  className={classes.typeDetail}
                  style={{ marginLeft: '10px' }}
                >
                  <p>
                    &#x2022; Impact of known or planned new credit issuances/
                    redemptions along the credit curve not yet reflected in
                    released financial statements through short and long term
                    debt
                  </p>
                  <p>
                    &#x2022; Impact of potential acquisitions or divestments
                    through target share price of the surviving entity
                  </p>
                  <p>
                    &#x2022; Enhanced risks in economic, macro or policy
                    environments through equity volatility as an input. For
                    issuers with liquid single stock options, investors have
                    often relied on implied volatility as a more predictive
                    input for future simulations
                  </p>

                  <span className={classes.typeDetail}>
                    You can adjust one or a combination of these variables. This
                    is done by moving the slider either positively or
                    negatively, in increments of 1% up to 50%, once you are
                    happy with the new “what-if” conditions press calculate.
                    This will produce a new credit stress measure (CSM) based
                    off your new what-if scenario and adjust the peer
                    comparisons across all the available variables.
                  </span>
                </div>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Step 2: </span>
                <span className={classes.typeDetail}>
                  This allows you to make further fine-grained adjustments to
                  the two modelled numbers which are not observable, Market
                  Value of Assets & Market Volatility of Assets, calculated
                  through the changes you make in step 1. Adjust them further to
                  create or enrich a new credit stress measure (CSM). These
                  modelled numbers are then updated versus peers, so the new
                  values can be seen in context, allowing you to identify
                  specific idiosyncratic credit drivers.
                </span>
              </div>

              <br />
              <div className={classes.detailContent}>
                <span className={classes.typeDetail}>
                  At any point if you want to start again, just press the reset
                  button and run your new what-if scenario, repeating step 1 and
                  step 2
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(WhatIfDataTooltipModal)
